import { useEffect, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import { parseCoordinates } from '../../../domain/DeviceLocation';

export interface LocationOption {
  key: string;
  title: string;
  detail: string;
  lat: number;
  lng: number;
}

const DEBOUNCE_MS = 350;

// Buscar una ubicación escribiendo: coordenadas pegadas a mano o el nombre
// de una localidad/paraje (Open-Meteo, sin API key). Espera a que se deje de
// tipear para no consultar en cada tecla.
export function useLocationSearch() {
  const { weather } = useRepositories();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const text = query.trim();
    setError(null);
    const coords = parseCoordinates(text);
    if (coords) {
      setOptions([
        {
          key: 'coords',
          title: `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
          detail: 'Usar estas coordenadas',
          ...coords,
        },
      ]);
      setSearching(false);
      return;
    }
    if (text.length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      weather
        .searchCities(text)
        .then((results) => {
          if (cancelled) return;
          setOptions(
            results.map((r) => ({
              key: String(r.id),
              title: r.name,
              detail: [r.admin1, r.country].filter(Boolean).join(', '),
              lat: r.latitude,
              lng: r.longitude,
            }))
          );
        })
        .catch(() => !cancelled && setError('No se pudo buscar. Probá de nuevo o marcá en el mapa.'))
        .finally(() => !cancelled && setSearching(false));
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, weather]);

  const clear = () => {
    setQuery('');
    setOptions([]);
  };

  return { query, setQuery, options, searching, error, clear };
}
